import { useEffect, useState } from "react";
import { Check, Globe, ImageOff, Loader2, Search, Sparkles } from "lucide-react";
import { Button, Card } from "@/ui";
import { cn } from "@/lib/cn";
import { useUpdatePage } from "@/data/hooks";
import { aiAssist, type Page } from "@/data/services";

/**
 * SeoTab — the Make builder's "SEO" tab. Edits the page-level search + social
 * metadata (title / slug / meta description / OG image) that the live page
 * renders into <head>, with a live Google-style SERP preview and a social card
 * preview so the marketer sees exactly what search engines and link unfurls show.
 *
 * Saves via PATCH /pages/{id} (useUpdatePage). The builder's `page` is held in
 * local state (not a refetched query), so dirty/saved tracking is done against a
 * local baseline that we advance on each successful save.
 */

/** Meta-description sweet spot — Google typically truncates around here. */
const DESC_IDEAL_MIN = 150;
const DESC_IDEAL_MAX = 160;
/** Where the counter starts nudging amber (getting close to the ideal window). */
const DESC_APPROACH = 130;

const INPUT_CLASS =
  "w-full rounded-btn border border-hairline bg-paper px-3 py-2 text-[14px] text-ink outline-none transition-colors placeholder:text-subtle focus:border-accent/50 dark:bg-white/5";

export function SeoTab({
  page,
  projectId,
}: {
  page: Page | undefined;
  projectId: string | undefined;
}) {
  if (!page) {
    return (
      <Card className="grid place-items-center px-6 py-12 text-center">
        <span className="grid h-10 w-10 place-items-center rounded-full bg-paper-2 text-subtle dark:bg-white/5">
          <Search className="h-5 w-5" strokeWidth={1.5} />
        </span>
        <p className="mt-3 text-[14px] font-medium text-ink-strong">Pick a page to edit its SEO</p>
        <p className="mt-1 max-w-[32ch] text-[13px] leading-relaxed text-muted">
          Choose a page from the picker above and you can tune its search and social preview here.
        </p>
      </Card>
    );
  }
  // Keyed by page id so switching pages remounts with a fresh, correctly-seeded form.
  return <SeoForm key={page.id} page={page} projectId={projectId} />;
}

/* --------------------------------- form ----------------------------------- */
function SeoForm({ page, projectId }: { page: Page; projectId: string | undefined }) {
  const update = useUpdatePage(projectId);

  const [title, setTitle] = useState(page.title);
  const [slug, setSlug] = useState(page.slug);
  const [description, setDescription] = useState(page.seo_description ?? "");
  const [ogImage, setOgImage] = useState(page.og_image_url ?? "");

  // Baseline = last persisted values. Advances on save so Save disables / shows
  // "Saved" without depending on the builder refetching its page.
  const [baseline, setBaseline] = useState({
    title: page.title,
    slug: page.slug,
    description: page.seo_description ?? "",
    ogImage: page.og_image_url ?? "",
  });

  // If the underlying page prop changes (e.g. published elsewhere), reseed.
  useEffect(() => {
    setTitle(page.title);
    setSlug(page.slug);
    setDescription(page.seo_description ?? "");
    setOgImage(page.og_image_url ?? "");
    setBaseline({
      title: page.title,
      slug: page.slug,
      description: page.seo_description ?? "",
      ogImage: page.og_image_url ?? "",
    });
  }, [page.title, page.slug, page.seo_description, page.og_image_url]);

  const dirty =
    title !== baseline.title ||
    slug !== baseline.slug ||
    description !== baseline.description ||
    ogImage !== baseline.ogImage;

  const save = () => {
    if (!dirty || update.isPending) return;
    update.mutate(
      {
        pageId: page.id,
        body: { title, slug, seo_description: description, og_image_url: ogImage },
      },
      {
        onSuccess: () => setBaseline({ title, slug, description, ogImage }),
      },
    );
  };

  // AI-generated metadata from the page's visible copy (#5).
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const generate = async () => {
    if (aiLoading) return;
    setAiLoading(true);
    setAiError(null);
    try {
      const r = await aiAssist.seo({ page_id: page.id });
      if (r.ok && r.seo) {
        setTitle(r.seo.title);
        setDescription(r.seo.description);
      } else {
        setAiError(r.error || "Couldn’t generate metadata");
      }
    } catch {
      setAiError("Couldn’t reach the AI service");
    } finally {
      setAiLoading(false);
    }
  };

  const descLen = description.length;
  const descTone =
    descLen > DESC_IDEAL_MAX
      ? "text-accent"
      : descLen >= DESC_APPROACH
        ? "text-amber-500"
        : "text-subtle";
  const justSaved = update.isSuccess && !dirty;

  return (
    <div className="space-y-5">
      {/* Live previews up top — the payoff the form feeds. */}
      <SerpPreview
        title={title}
        description={description}
        domain={page.domain}
        slug={slug}
      />
      <SocialPreview
        title={title}
        description={description}
        domain={page.domain}
        ogImage={ogImage}
      />

      {/* The form. */}
      <Card className="space-y-4 p-4">
        <div className="flex items-center justify-between gap-2">
          <p className="eyebrow px-0.5">Search &amp; social</p>
          <button
            type="button"
            onClick={generate}
            disabled={aiLoading}
            title="Generate title & description from this page's content"
            className="inline-flex items-center gap-1.5 rounded-btn border border-hairline px-2.5 py-1 text-[12px] font-medium text-accent transition-colors hover:bg-accent-soft disabled:opacity-50"
          >
            {aiLoading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.75} />
            ) : (
              <Sparkles className="h-3.5 w-3.5" strokeWidth={1.75} />
            )}
            {aiLoading ? "Writing…" : "Generate with AI"}
          </button>
        </div>
        {aiError && <p className="-mt-2 text-[12px] text-accent">{aiError}</p>}

        {/* SEO title */}
        <label className="block">
          <span className="mb-1 block text-[11px] font-medium text-ink">SEO title</span>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Open an account in 30 seconds"
            className={INPUT_CLASS}
            maxLength={120}
          />
        </label>

        {/* URL slug */}
        <label className="block">
          <span className="mb-1 block text-[11px] font-medium text-ink">URL slug</span>
          <div className="flex items-center gap-1.5 rounded-btn border border-hairline bg-paper px-2.5 focus-within:border-accent/50 dark:bg-white/5">
            <span className="shrink-0 font-mono text-[12px] text-subtle">/</span>
            <input
              value={slug.replace(/^\//, "")}
              onChange={(e) => setSlug(e.target.value.replace(/^\/+/, ""))}
              placeholder="pricing"
              className="w-full min-w-0 bg-transparent py-2 font-mono text-[13px] text-ink outline-none placeholder:text-subtle"
            />
          </div>
        </label>

        {/* Meta description + live counter */}
        <label className="block">
          <span className="mb-1 flex items-center justify-between text-[11px] font-medium text-ink">
            Meta description
            <span className={cn("font-mono text-[10px]", descTone)}>
              {descLen}
              <span className="text-subtle"> / ~{DESC_IDEAL_MIN}–{DESC_IDEAL_MAX}</span>
            </span>
          </span>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            placeholder="A 1–2 sentence summary search engines show under the title. Aim for the sweet spot so it isn't cut off."
            className={cn(INPUT_CLASS, "resize-none leading-relaxed")}
            maxLength={320}
          />
          <span className="mt-1 block text-[10px] leading-tight text-subtle">
            {descLen === 0
              ? "Empty — engines will pull text from the page instead."
              : descLen > DESC_IDEAL_MAX
                ? "A bit long — Google may truncate this in results."
                : descLen >= DESC_IDEAL_MIN
                  ? "Nice — right in the sweet spot."
                  : "Looking good. ~150–160 characters reads best."}
          </span>
        </label>

        {/* OG image URL */}
        <label className="block">
          <span className="mb-1 block text-[11px] font-medium text-ink">OG image URL</span>
          <input
            value={ogImage}
            onChange={(e) => setOgImage(e.target.value)}
            placeholder="https://…/share-card.png"
            className={cn(INPUT_CLASS, "font-mono text-[12px]")}
            inputMode="url"
          />
          <span className="mt-1 block text-[10px] leading-tight text-subtle">
            Shown when the page is shared. 1200×630 PNG/JPG works best.
          </span>
        </label>

        {/* Save */}
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <Button variant="coral" onClick={save} disabled={!dirty || update.isPending}>
            {update.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.75} />
                Saving…
              </>
            ) : justSaved ? (
              <>
                <Check className="h-4 w-4" strokeWidth={1.75} />
                Saved
              </>
            ) : (
              "Save SEO"
            )}
          </Button>
          {dirty && !update.isPending && (
            <span className="text-[12px] text-muted">Unsaved changes</span>
          )}
          {update.isError && (
            <span className="text-[12px] text-accent">
              {update.error instanceof Error ? update.error.message : "Couldn't save. Try again."}
            </span>
          )}
        </div>
      </Card>
    </div>
  );
}

/* ----------------------------- SERP preview ------------------------------- */
/** A Google-style organic result that updates live as the form is edited. */
function SerpPreview({
  title,
  description,
  domain,
  slug,
}: {
  title: string;
  description: string;
  domain?: string;
  slug: string;
}) {
  const host = (domain && domain.trim()) || "yourdomain.com";
  const segs = slug
    .split("/")
    .map((s) => s.trim())
    .filter(Boolean);
  return (
    <div className="overflow-hidden rounded-card border border-hairline bg-paper-2 dark:bg-white/5">
      <div className="flex items-center gap-1.5 border-b border-hairline px-3 py-2">
        <Search className="h-3.5 w-3.5 text-subtle" strokeWidth={1.75} />
        <span className="eyebrow">Search preview</span>
      </div>
      <div className="bg-paper p-4 dark:bg-white/[0.02]">
        {/* favicon + breadcrumb url */}
        <div className="flex items-center gap-2">
          <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-paper-2 text-subtle dark:bg-white/10">
            <Globe className="h-3.5 w-3.5" strokeWidth={1.75} />
          </span>
          <span className="min-w-0 truncate text-[12px] leading-tight text-ink">
            {host}
            <span className="text-subtle">
              {segs.length ? ` › ${segs.join(" › ")}` : ""}
            </span>
          </span>
        </div>
        {/* title link */}
        <p className="mt-1.5 truncate text-[18px] leading-snug text-[#1a0dab] dark:text-[#8ab4f8]">
          {title || "Your page title"}
        </p>
        {/* meta description */}
        <p className="mt-1 line-clamp-2 text-[13px] leading-relaxed text-muted">
          {description ||
            "Add a meta description to control the snippet search engines show beneath your title."}
        </p>
      </div>
    </div>
  );
}

/* ---------------------------- social card preview ------------------------- */
/** A compact Open Graph link-unfurl card (the kind Slack / X / iMessage show). */
function SocialPreview({
  title,
  description,
  domain,
  ogImage,
}: {
  title: string;
  description: string;
  domain?: string;
  ogImage: string;
}) {
  const host = (domain && domain.trim()) || "yourdomain.com";
  const [broken, setBroken] = useState(false);
  const url = ogImage.trim();
  const showImg = !!url && !broken;

  // Reset the error flag whenever the url changes so a fixed link re-renders.
  useEffect(() => setBroken(false), [url]);

  return (
    <div className="overflow-hidden rounded-card border border-hairline bg-paper-2 dark:bg-white/5">
      <div className="border-b border-hairline px-3 py-2">
        <span className="eyebrow">Social card</span>
      </div>
      <div className="p-3">
        <div className="overflow-hidden rounded-card border border-hairline bg-paper dark:bg-white/[0.02]">
          {/* image (1.91:1) or placeholder */}
          <div className="relative aspect-[1.91/1] w-full bg-paper-2 dark:bg-white/5">
            {showImg ? (
              <img
                src={url}
                alt=""
                onError={() => setBroken(true)}
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="grid h-full w-full place-items-center text-subtle">
                <ImageOff className="h-7 w-7" strokeWidth={1.25} />
                <span className="mt-1.5 text-[11px]">
                  {url ? "Couldn't load image" : "OG image preview"}
                </span>
              </div>
            )}
          </div>
          {/* meta */}
          <div className="border-t border-hairline px-3.5 py-3">
            <p className="font-mono text-[10px] uppercase tracking-wide text-subtle">{host}</p>
            <p className="mt-1 truncate text-[14px] font-semibold tracking-tight text-ink-strong">
              {title || "Your page title"}
            </p>
            <p className="mt-0.5 line-clamp-2 text-[12px] leading-relaxed text-muted">
              {description || "Your page description will appear here when shared."}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
